/**
 * `LakiruutuRoute` — the `/lakiruutu` member surface (029 US2 / FR-007).
 *
 * Renders the published `video_listing` content as a vertical list to proto
 * parity (the proto's `Lakiruutu.tsx`): a header, then one
 * `LakiruutuVideoCard` per entry (96x80 thumbnail + Play link-out, title,
 * collapsible HTML description via `SanitizedHtml`). This replaces the 017 US3
 * honest-demo stub (a fixed h1 + placeholder paragraph that read no content
 * port); the URL, menu wiring, and accessibility contract are preserved.
 *
 * Data through the port (P3 / FR-012): the route reads ONLY `useVideoListings`
 * (the `ContentReaderPort` hook) — it never imports a concrete adapter (enforced
 * by the `no-content-adapter-import` architecture check, which now scans
 * `content/routes/`).
 *
 * State matrix (FR-014):
 *   - `isPending`  -> a loading skeleton (`role="status"`).
 *   - `isError`    -> re-throw so the route's `PortErrorBoundary` (wired by the
 *                     `portBoundary: 'content'` route descriptor) renders
 *                     `ContentUnavailable`. Unlike the Home strip's inline
 *                     degradation, `/lakiruutu` is a dedicated surface, so a read
 *                     failure goes through the boundary (mirrors `ArticleListPage`).
 *   - `[]`         -> the defined empty state "Ei videoita saatavilla.".
 *   - populated    -> the list of video cards, ordered by `order` (the adapter
 *                     sorts; the route preserves it).
 *
 * (024 D-auto: the video read shape no longer carries a `category` field.)
 * Video playback is link-out, not an in-app iframe modal (research D9 / human
 * R2) — see `LakiruutuVideoCard`.
 *
 * Spec references: `specs/029-cms-driven-member-surfaces/` spec FR-007 / FR-014,
 * research D5 + D9, data-model "Read shape 2"; FR-049 (SanitizedHtml).
 */

import type { JSX } from 'react';
import { useTranslate } from '../../i18n/useTranslate.js';
import { useVideoListings } from '../hooks/useVideoListings.js';
import { DetailSurfaceShell } from './detailSurfaceShell.js';
import { LakiruutuVideoCard } from './LakiruutuVideoCard.js';

const headingClasses = 'm-0 text-2xl font-bold text-foreground';
const listClasses = 'flex flex-col gap-4';
const emptyStateClasses = 'm-0 py-8 text-center text-sm text-muted-foreground';
const skeletonWrapperClasses = 'flex flex-col gap-4';
const skeletonCardClasses = 'h-32 w-full animate-pulse rounded-lg bg-muted';

export function LakiruutuRoute(): JSX.Element {
  const t = useTranslate();
  const query = useVideoListings();

  if (query.isError) {
    // Re-throw into the route's PortErrorBoundary (portBoundary: 'content').
    // The `?? new Error(...)` guard mirrors ArticleListPage / ArticleDetailPage:
    // React Query's type union permits `isError` with a `null` error.
    throw (
      query.error ??
      new Error('useVideoListings resolved to an error state without an Error instance')
    );
  }

  return (
    <DetailSurfaceShell as="section" ariaLabelledBy="lakiruutu-title" fallbackTo="/">
      <div className="flex flex-col gap-6">
        <h1 id="lakiruutu-title" className={headingClasses}>
          {t('routes.lakiruutu.title')}
        </h1>
        {query.isPending ? (
          <div
            role="status"
            aria-busy="true"
            aria-label={t('common.loading')}
            data-testid="lakiruutu-skeleton"
            className={skeletonWrapperClasses}
          >
            <span aria-hidden="true" className={skeletonCardClasses} />
            <span aria-hidden="true" className={skeletonCardClasses} />
          </div>
        ) : query.data.length === 0 ? (
          <p className={emptyStateClasses}>{t('routes.lakiruutu.empty')}</p>
        ) : (
          <div className={listClasses}>
            {query.data.map((video) => (
              <LakiruutuVideoCard key={video.id} video={video} t={t} />
            ))}
          </div>
        )}
      </div>
    </DetailSurfaceShell>
  );
}
